import React from 'react';
import { Link } from 'react-router-dom';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: {
    value: string;
    isPositive: boolean;
    label: string;
  };
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'info';
  href?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  color = 'primary',
  href,
}) => {
  const colorClasses: Record<string, { iconBg: string; iconText: string; accent: string }> = {
    primary:   { iconBg: 'bg-primary-50',   iconText: 'text-primary-600',   accent: 'text-primary-700' },
    secondary: { iconBg: 'bg-secondary-50',  iconText: 'text-secondary-600', accent: 'text-secondary-700' },
    success:   { iconBg: 'bg-success-50',    iconText: 'text-success-600',   accent: 'text-success-700' },
    warning:   { iconBg: 'bg-warning-50',    iconText: 'text-warning-600',   accent: 'text-warning-700' },
    info:      { iconBg: 'bg-info-50',       iconText: 'text-info-600',      accent: 'text-info-700' },
  };

  const colors = colorClasses[color];

  const content = (
    <div className="relative">
      {/* Header row: icon + title */}
      <div className="flex items-center gap-3 mb-3">
        <div className={`inline-flex p-2.5 ${colors.iconBg} rounded-xl ${colors.iconText} ring-1 ring-inset ring-current/10`}>
          {icon}
        </div>
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-monday-text-muted">
          {title}
        </p>
      </div>

      {/* Value */}
      <p className="text-3xl font-extrabold tracking-tight text-monday-text mb-1">
        {value}
      </p>

      {/* Trend or Subtitle */}
      {trend ? (
        <div className="flex items-center gap-1 text-xs">
          <svg
            className={`w-3.5 h-3.5 ${trend.isPositive ? 'text-success-500' : 'text-error-500'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            {trend.isPositive ? (
              <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            ) : (
              <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
            )}
          </svg>
          <span className={`font-semibold ${trend.isPositive ? 'text-success-500' : 'text-error-500'}`}>
            {trend.value}
          </span>
          <span className="text-monday-text-muted">{trend.label}</span>
        </div>
      ) : subtitle ? (
        <p className="text-xs text-monday-text-muted">{subtitle}</p>
      ) : null}
    </div>
  );

  const classes =
    'bg-white/95 p-6 rounded-xl border border-monday-border-light shadow hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 group';

  if (href) {
    return (
      <Link to={href} className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <div className={classes}>
      {content}
    </div>
  );
};

export default StatCard;
